/**
 * Live subscription to the signed-in user's chosen currency (set once in
 * profile → Settings → Shipping & Currency, per the multi-currency work
 * in tasks #252-#258). Returns the resolved CurrencyInfo so callers can
 * render `{currency.symbol}` in price inputs and know when to say things
 * like "prices shown to buyers as €X".
 *
 * The save side of destash listings already denormalizes profile.currency
 * onto items via `services/destashDenormalization.ts:buildDestashFields`.
 * This hook is purely for DISPLAY parity so a seller in EUR doesn't see
 * `$` next to their price input.
 *
 * Defaults to USD while auth/profile are loading OR when the profile has
 * no currency field yet (legacy users who haven't opened Settings since
 * the multi-currency migration). That matches the save-side default in
 * destashDenormalization.ts:45.
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';
import { getCurrencyInfo, type CurrencyInfo } from '../constants/currencies';

export function useSellerCurrency(): CurrencyInfo {
  const { uid } = useAuth();
  const [code, setCode] = useState<string>('USD');

  useEffect(() => {
    if (!uid) {
      setCode('USD');
      return;
    }
    // onSnapshot (not getDoc) so a Settings save in another tab / device
    // flows to the currently-open add modal immediately.
    const unsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const data = snap.data();
        const c = typeof data?.currency === 'string' ? data.currency : 'USD';
        setCode(c);
      },
      (err) => {
        console.warn('[useSellerCurrency] profile read failed:', err?.message ?? err);
        setCode('USD');
      }
    );
    return unsub;
  }, [uid]);

  return getCurrencyInfo(code);
}
